'use client'

import { useReducedMotion } from 'motion/react'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { StoryMarkdown } from '@/components/story-markdown'
import {
    type Box,
    carryLayers,
    contains,
    dirFrom,
    fitTransform,
    formulaReveal,
    frameAt,
    lerpBox,
    plainLayers,
    unionBox,
} from '@/features/landing/method/carry'
import { CARRY_FRAMES } from '@/features/landing/method/carry-frames'
import { IndexStepper } from '@/features/landing/method/index-stepper'
import { StrokeDraw } from '@/features/landing/stroke/stroke-draw'
import { useTrackProgress } from '@/features/landing/walk/use-track-progress'

export interface MethodFigureFrame {
    character: string
    frame: number
    keyword: string
    story: string | null
    strokePaths: string[]
    viewBox: string
}

const W = 4.2 // stroke width in viewBox units; scaled layers divide by their scale
const SLIDE = 26 // viewBox units the new part's guide slides in from

interface Geometry {
    prevBox: Box // the previous kanji's box in its own glyph
    cBox: Box // the carried strokes' box inside the composed glyph
    nBox: Box // the new part's box inside the composed glyph
}

// One sticky figure scrubbed through four frames (spec §3.1). Geometry
// (bounding boxes) is measured once from a hidden copy of every glyph.
export function MethodFigure({ frames }: { frames: MethodFigureFrame[] }) {
    const track = useRef<HTMLDivElement>(null)
    const measure = useRef<SVGSVGElement>(null)
    const [geo, setGeo] = useState<Geometry[] | null>(null)
    const p = useTrackProgress(track)
    const reduce = useReducedMotion()
    const { i, t: raw } = frameAt(p, frames.length)
    // Reduced motion: each reached step shows its finished kanji, no scrub.
    const t = reduce ? 1 : raw

    useLayoutEffect(() => {
        const svg = measure.current
        if (!svg) return
        const boxOf = (sel: string): Box => {
            const els = Array.from(svg.querySelectorAll<SVGPathElement>(sel))
            return unionBox(
                els.map((e) => {
                    const b = e.getBBox()
                    return { x: b.x, y: b.y, w: b.width, h: b.height }
                })
            )
        }
        const strokesOf = (k: number, js: number[]) =>
            js.map((j) => `[data-f="${k}"][data-s="${j}"]`).join(',')
        setGeo(
            frames.map((_, k) => {
                const all = boxOf(`[data-f="${k}"]`)
                if (k === 0) return { prevBox: all, cBox: all, nBox: all }
                const f = CARRY_FRAMES[k]!
                return {
                    prevBox: boxOf(`[data-f="${k - 1}"]`),
                    cBox: boxOf(strokesOf(k, f.carried)),
                    nBox: boxOf(strokesOf(k, f.part!.indices)),
                }
            })
        )
    }, [frames])

    const cur = frames[i]!
    const prev = i > 0 ? frames[i - 1]! : null
    const def = CARRY_FRAMES[i]!
    const carried = new Set(def.carried)
    const step = prev ? carryLayers(t, cur.strokePaths.length, carried) : null
    const shown = formulaReveal(prev ? t : 1)
    const label = !prev || step!.labelFlipped ? cur : prev

    let stage: ReactNode
    if (!prev || !step || !geo) {
        stage = (
            <StrokeDraw
                strokePaths={cur.strokePaths}
                viewBox={cur.viewBox}
                drawn={plainLayers(i === 0 ? t : 1, cur.strokePaths.length)}
                strokeWidth={W}
                className="absolute inset-0 size-full text-ink"
            />
        )
    } else {
        const s = step
        const g = geo[i]!
        const box = lerpBox(g.prevBox, g.cBox, s.move)
        const fit = fitTransform(g.prevBox, box)
        const surround = contains(g.nBox, g.cBox)
        const k = 1 + 0.16 * (1 - s.move)
        const d = dirFrom(g.cBox, g.nBox)
        const c = { x: g.nBox.x + g.nBox.w / 2, y: g.nBox.y + g.nBox.h / 2 }
        const ghostTransform = surround
            ? `translate(${c.x} ${c.y}) scale(${k}) translate(${-c.x} ${-c.y})`
            : `translate(${d.dx * SLIDE * (1 - s.move)} ${d.dy * SLIDE * (1 - s.move)})`
        const all = cur.strokePaths.map(() => 1)
        const prevAll = prev.strokePaths.map(() => 1)
        stage = (
            <>
                {/* composed guide for the carried strokes: takes over from the travelling kanji */}
                <StrokeDraw
                    strokePaths={cur.strokePaths}
                    viewBox={cur.viewBox}
                    drawn={all}
                    opacity={cur.strokePaths.map((_, j) =>
                        carried.has(j) ? s.cGuideOpacity : 0
                    )}
                    strokeWidth={W}
                    className="absolute inset-0 size-full text-ink/20"
                />
                {/* the new part's guide, sliding in from its side */}
                <svg
                    viewBox={cur.viewBox}
                    className="absolute inset-0 size-full text-ink/20"
                    aria-hidden
                >
                    <g transform={ghostTransform}>
                        <StrokeDraw
                            strokePaths={cur.strokePaths}
                            viewBox={cur.viewBox}
                            drawn={all}
                            opacity={s.ghostOpacity}
                            strokeWidth={surround ? W / k : W}
                            className="size-full"
                        />
                    </g>
                </svg>
                {/* the travelling kanji: ink → guide, fitted to its slot */}
                <svg
                    viewBox={prev.viewBox}
                    className="absolute inset-0 size-full"
                    aria-hidden
                >
                    <g
                        transform={fit.transform}
                        style={{ opacity: 1 - s.grey }}
                        className="text-ink"
                    >
                        <StrokeDraw
                            strokePaths={prev.strokePaths}
                            viewBox={prev.viewBox}
                            drawn={prevAll}
                            strokeWidth={W / fit.scale}
                            className="size-full"
                        />
                    </g>
                    <g
                        transform={fit.transform}
                        style={{ opacity: s.grey * (1 - s.hand) }}
                        className="text-ink/20"
                    >
                        <StrokeDraw
                            strokePaths={prev.strokePaths}
                            viewBox={prev.viewBox}
                            drawn={prevAll}
                            strokeWidth={W / fit.scale}
                            className="size-full"
                        />
                    </g>
                </svg>
                {/* the composed kanji, written in true stroke order */}
                <StrokeDraw
                    strokePaths={cur.strokePaths}
                    viewBox={cur.viewBox}
                    drawn={s.drawn}
                    opacity={s.opacity}
                    strokeWidth={W}
                    className="absolute inset-0 size-full text-ink"
                />
            </>
        )
    }

    return (
        <div ref={track} className="relative h-[400vh]">
            {/* The scrubbed figure shows one frame at a time; screen readers
                get the whole sequence at once. */}
            <ol className="sr-only" aria-label="The frames, in order">
                {frames.map((f) => (
                    <li key={f.frame}>
                        Frame {f.frame}, {f.character}, {f.keyword}
                        {f.story ? `. ${f.story.replace(/\*+/g, '')}` : null}
                    </li>
                ))}
            </ol>
            {/* hidden copy of every glyph for bounding boxes */}
            <svg
                ref={measure}
                viewBox="0 0 109 109"
                className="absolute size-px opacity-0"
                aria-hidden
            >
                {frames.map((f, k) =>
                    f.strokePaths.map((d, j) => (
                        <path
                            key={`${k}-${j}`}
                            d={d}
                            data-f={k}
                            data-s={j}
                            fill="none"
                            stroke="black"
                        />
                    ))
                )}
            </svg>
            {/* The figure and its caption sit in the page frame (max-w-6xl);
                full-bleed, a wide monitor put a viewport between them. */}
            <div className="sticky top-16 h-[calc(100svh-4rem)] px-5 md:px-10">
                <div className="mx-auto grid h-full max-w-6xl grid-rows-[minmax(0,1fr)_auto] items-center gap-6 max-md:pb-6 md:grid-cols-2 md:grid-rows-1 md:gap-12">
                    {/* On phones the square is sized by the height as well as
                        the width — the viewport below the rail less the
                        caption's depth (index, title, a five-line story), the
                        formula row and the row's bottom padding, since the
                        grid pins the caption to the box's bottom — and the
                        formula's row is in flow,
                        so a short viewport (the URL bar showing) shrinks the
                        glyph instead of running the story off the bottom or
                        the index into the formula. From md the column's
                        width sizes it. */}
                    <div className="mx-auto w-full max-w-[min(100%,440px,calc(100svh-4rem-25.5rem))] max-md:pb-16 md:max-w-[min(100%,440px)]">
                        <figure className="relative aspect-square w-full">
                            <figcaption className="absolute -top-7 left-0 font-serif text-lp-eyebrow-lg tracking-lp-28 text-muted-fg uppercase tabular-nums">
                                Frame {label.frame}
                            </figcaption>
                            {stage}
                            <div
                                className="absolute inset-x-0 -bottom-16 flex items-end justify-center gap-5 font-serif"
                                aria-hidden
                            >
                                {prev && def.part ? (
                                    <>
                                        <Part
                                            char={prev.character}
                                            name={prev.keyword}
                                            on={shown[0]}
                                        />
                                        <Op on={shown[1]}>+</Op>
                                        <Part
                                            char={def.part.character}
                                            name={def.part.name}
                                            on={shown[2]}
                                        />
                                        <Op on={shown[3]}>=</Op>
                                        <Part
                                            char={cur.character}
                                            name={cur.keyword}
                                            on={shown[4]}
                                        />
                                    </>
                                ) : null}
                            </div>
                        </figure>
                    </div>
                    <div className="self-start md:self-center">
                        <IndexStepper frames={frames} i={i} t={t} />
                        <div
                            key={label.frame}
                            className="mt-8 motion-safe:animate-in motion-safe:duration-300 motion-safe:fade-in motion-safe:slide-in-from-bottom-2"
                        >
                            <h3 className="font-serif text-lp-title text-ink md:text-lp-title-md">
                                {label.keyword}
                            </h3>
                            {label.story ? (
                                <StoryMarkdown className="mt-4 max-w-[36ch] font-serif text-lp-lede text-body-fg [&_strong]:font-semibold [&_strong]:text-ink">
                                    {label.story}
                                </StoryMarkdown>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function Part({ char, name, on }: { char: string; name: string; on: boolean }) {
    return (
        <span
            className={
                on
                    ? 'text-center transition-opacity duration-300'
                    : 'text-center opacity-0 transition-opacity duration-300'
            }
        >
            <b className="block font-mincho text-[40px] leading-none font-normal text-ink">
                {char}
            </b>
            <i className="mt-1.5 block text-lp-eyebrow-md tracking-lp-22 text-muted-fg uppercase not-italic">
                {name}
            </i>
        </span>
    )
}

function Op({ children, on }: { children: string; on: boolean }) {
    return (
        <em
            className={
                on
                    ? 'pb-5 text-[22px] text-muted-fg not-italic transition-opacity duration-300'
                    : 'pb-5 text-[22px] text-muted-fg not-italic opacity-0 transition-opacity duration-300'
            }
        >
            {children}
        </em>
    )
}
